import { useEffect, useRef } from 'react';

import { wxCopilotContextManager } from 'utils/wxCopilotContextManager';

let ownerSequence = 0;

export function useWxCopilotApplicantTableContext({
  openingId,
  openingName,
  currentStageId,
  currentStageName,
  currentStageType,
  stages,
  applicants,
  totalApplicants,
  selectedApplicantIds,
  bulkSelect,
}) {
  const ownerIdRef = useRef(null);
  if (ownerIdRef.current === null) {
    ownerSequence += 1;
    ownerIdRef.current = `applicant-table-context-owner-${ownerSequence}`;
  }
  const ownerId = ownerIdRef.current;

  // Unregistering runs in its own effect, keyed only on ownerId (stable for
  // the component's lifetime), so its cleanup fires on a true unmount only —
  // not on every dependency change below. A cleanup that ran on every
  // re-render deleted this owner from the manager's map an instant before
  // the data effect re-added it, which briefly promoted another mounted
  // table as current and, when this owner's own re-render didn't carry a
  // genuine selection change, never handed ownership back.
  useEffect(() => {
    return () => {
      wxCopilotContextManager.setApplicantTableContext(undefined, ownerId);
    };
  }, [ownerId]);

  useEffect(() => {
    // Some surfaces (e.g. the Scheduled Sessions roster) have no single
    // opening to report — a roster can span multiple funnels — but still
    // need to report their own applicant selection to Cue.
    if ((openingId && openingName) || applicants?.length) {
      wxCopilotContextManager.setApplicantTableContext(
        {
          openingId,
          openingName,
          currentStageId,
          currentStageName,
          currentStageType,
          stages,
          applicants,
          totalApplicants,
          selectedApplicantIds,
          bulkSelect,
        },
        ownerId,
      );
    } else {
      // A genuine true->false transition (e.g. a roster's applicants drain
      // to 0 while still mounted) must clear this owner's stale payload —
      // this is not the per-render cleanup removed above, it only runs when
      // the condition itself changes.
      wxCopilotContextManager.setApplicantTableContext(undefined, ownerId);
    }
  }, [
    openingId,
    openingName,
    currentStageId,
    currentStageName,
    currentStageType,
    stages,
    applicants,
    totalApplicants,
    selectedApplicantIds,
    bulkSelect,
    ownerId,
  ]);
}
